import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useCart } from "./CartContext";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// Create calculator context
const CalculatorContext = createContext();

/**
 * CalculatorProvider component for managing calculator state
 * @param {Object} props - Component props
 * @returns {JSX.Element} CalculatorProvider component
 */
export const CalculatorProvider = ({ children }) => {
  const { t } = useTranslation("products");
  const { cart, addToCart, updateCartItem } = useCart();
  const [calculatorItems, setCalculatorItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Get the quantity of a variation already in the cart
   * @param {string} variationId - Variation ID
   * @returns {number} Quantity in cart
   */
  const getCartQuantityForVariation = useCallback(
    (variationId) => {
      if (!cart || !cart.items) return 0;

      // Find items in cart that match this variation
      const matchingItems = cart.items.filter((item) => {
        return item.options?.variationId === variationId;
      });

      // Sum up quantities
      return matchingItems.reduce((total, item) => total + item.quantity, 0);
    },
    [cart]
  );

  /**
   * Check available stock for a variation
   * @param {Object} variation - Variation object
   * @param {number} requestedQuantity - Requested quantity
   * @param {string} currentItemId - ID of the current item being updated (to exclude from calculation)
   * @returns {object} Available stock info
   */
  const checkAvailableStock = useCallback(
    (variation, requestedQuantity, currentItemId = null) => {
      // Get cart quantity
      const cartQuantity = getCartQuantityForVariation(variation.id);

      // Get calculator quantity (excluding current item if it exists)
      const calculatorQuantity = calculatorItems.reduce((total, item) => {
        if (
          item.variation.id === variation.id &&
          item.variation.id !== currentItemId
        ) {
          return total + item.quantity;
        }
        return total;
      }, 0);

      // Calculate available stock
      const totalStock = parseInt(variation.stock);
      const availableStock = Math.max(
        0,
        totalStock - cartQuantity - calculatorQuantity
      );

      // Check if requested quantity exceeds available stock
      const isExceeded = requestedQuantity > availableStock;

      return {
        availableStock,
        cartQuantity,
        calculatorQuantity,
        totalStock,
        isExceeded,
        allowedQuantity: Math.min(requestedQuantity, availableStock),
      };
    },
    [calculatorItems, getCartQuantityForVariation]
  );

  /**
   * Add an item to the calculator
   * @param {Object} product - The parent product
   * @param {Object} variation - The selected variation
   * @param {number} quantity - Quantity to add
   */
  const addToCalculator = useCallback(
    (product, variation, quantity) => {
      // Ensure we have required data
      if (!product || !variation || !quantity) {
        setError(t("calculator.error.invalidProductData"));
        toast.error(t("calculator.error.addItemFailed"));
        return;
      }

      // Check if we already have this variation in the calculator
      const existingItemIndex = calculatorItems.findIndex(
        (item) => item.variation.id === variation.id
      );

      if (existingItemIndex >= 0) {
        // Update existing item
        setCalculatorItems((prevItems) => {
          const newItems = [...prevItems];
          const currentQuantity = prevItems[existingItemIndex].quantity;
          const newQty = currentQuantity + quantity;

          // Check if the new quantity exceeds available stock
          // Since we're updating an existing item, we need to exclude its current quantity from the check
          const variationCopy = { ...variation };
          const stockCheck = checkAvailableStock(
            variationCopy,
            newQty,
            variation.id
          );

          if (stockCheck.isExceeded) {
            const maxAdditional = Math.max(
              0,
              stockCheck.totalStock - stockCheck.cartQuantity - currentQuantity
            );
            toast.warning(
              t("calculator.warning.maxAdditionalAvailable", {
                count: maxAdditional,
              })
            );
            newItems[existingItemIndex].quantity =
              currentQuantity + maxAdditional;
          } else {
            newItems[existingItemIndex].quantity = newQty;
          }

          // Update line total
          newItems[existingItemIndex].lineTotal =
            parseFloat(newItems[existingItemIndex].variation.price) *
            newItems[existingItemIndex].quantity;

          return newItems;
        });
      } else {
        // Add new item
        // Check against available stock
        const stockCheck = checkAvailableStock(variation, quantity);

        if (stockCheck.isExceeded) {
          toast.warning(
            t("calculator.warning.stockAvailable", {
              count: stockCheck.availableStock,
            })
          );
          quantity = stockCheck.allowedQuantity;
        }

        if (quantity > 0) {
          setCalculatorItems((prevItems) => [
            ...prevItems,
            {
              product,
              variation,
              quantity,
              lineTotal: parseFloat(variation.price) * quantity,
            },
          ]);
        }
      }

      toast.success(t("calculator.success.itemAdded"));
    },
    [calculatorItems, checkAvailableStock, t]
  );

  /**
   * Update quantity of an item in the calculator
   * @param {string} variationId - Variation ID
   * @param {number} quantity - New quantity
   */
  const updateCalculatorItem = useCallback(
    (variationId, quantity) => {
      setCalculatorItems((prevItems) => {
        return prevItems.map((item) => {
          if (item.variation.id === variationId) {
            // Check against available stock
            const stockCheck = checkAvailableStock(
              item.variation,
              quantity,
              variationId
            );

            if (stockCheck.isExceeded) {
              toast.warning(
                t("calculator.warning.stockAvailableAfterCart", {
                  availableCount:
                    stockCheck.totalStock - stockCheck.cartQuantity,
                  cartCount: stockCheck.cartQuantity,
                })
              );
              quantity = stockCheck.allowedQuantity;
            }

            return {
              ...item,
              quantity,
              lineTotal: parseFloat(item.variation.price) * quantity,
            };
          }
          return item;
        });
      });
    },
    [checkAvailableStock, t]
  );

  /**
   * Remove an item from the calculator
   * @param {string} variationId - Variation ID
   */
  const removeFromCalculator = useCallback(
    (variationId) => {
      setCalculatorItems((prevItems) =>
        prevItems.filter((item) => item.variation.id !== variationId)
      );
      toast.success(t("calculator.success.itemRemoved"));
    },
    [t]
  );

  /**
   * Clear all items from the calculator
   */
  const clearCalculator = useCallback(() => {
    setCalculatorItems([]);
    toast.success(t("calculator.success.cleared"));
  }, [t]);

  /**
   * Add all calculator items to cart
   */
  const addAllToCart = useCallback(async () => {
    if (calculatorItems.length === 0) {
      toast.info(t("calculator.info.empty"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Process each item sequentially
      for (const item of calculatorItems) {
        // Check if this variation already exists in the cart
        const existingCartItem = cart?.items?.find(
          (cartItem) => cartItem.options?.variationId === item.variation.id
        );

        if (existingCartItem) {
          // If item exists in cart, update its quantity instead
          const newQuantity = existingCartItem.quantity + item.quantity;
          // Check if we have enough stock
          if (newQuantity <= item.variation.stock) {
            console.log("Updating existing cart item:", {
              itemId: existingCartItem.id,
              quantity: newQuantity,
            });
            // Use the updateCartItem function from CartContext
            await updateCartItem(existingCartItem.id, newQuantity);
          } else {
            toast.warning(
              t("calculator.warning.cannotAddMore", {
                quantity: item.quantity,
                name: item.variation.name,
                stock: item.variation.stock,
              })
            );
          }
        } else {
          // If item doesn't exist, add as new
          await addToCart(item.product.id, item.quantity, {
            variationId: item.variation.id,
            variationName: item.variation.name,
            isVariation: true,
          });
        }
      }

      // Clear calculator after adding all items to cart
      setCalculatorItems([]);
      toast.success(t("calculator.success.allAddedToCart"));
    } catch (error) {
      console.error("Error adding calculator items to cart:", error);
      setError(t("calculator.error.addAllToCartFailed"));
      toast.error(t("calculator.error.addSomeToCartFailed"));
    } finally {
      setLoading(false);
    }
  }, [calculatorItems, cart, addToCart, updateCartItem, t]);

  /**
   * Calculate the subtotal of all items in the calculator
   * @returns {number} Subtotal
   */
  const calculateSubtotal = useMemo(() => {
    return calculatorItems.reduce(
      (total, item) => total + parseFloat(item.variation.price) * item.quantity,
      0
    );
  }, [calculatorItems]);

  // Context value
  const value = useMemo(
    () => ({
      calculatorItems,
      loading,
      error,
      addToCalculator,
      updateCalculatorItem,
      removeFromCalculator,
      clearCalculator,
      addAllToCart,
      subtotal: calculateSubtotal,
      getCartQuantityForVariation,
      checkAvailableStock,
    }),
    [
      calculatorItems,
      loading,
      error,
      addToCalculator,
      updateCalculatorItem,
      removeFromCalculator,
      clearCalculator,
      addAllToCart,
      calculateSubtotal,
      getCartQuantityForVariation,
      checkAvailableStock,
    ]
  );

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
};

/**
 * Custom hook for using calculator context
 * @returns {Object} Calculator context value
 */
export const useCalculator = () => {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error("useCalculator must be used within a CalculatorProvider");
  }
  return context;
};

export default CalculatorContext;
